// The Work-home mark's PEN ROUTES: the pieces of the logo outline that
// `useWorkHomeMarkEntrance` draws in. Generated once from `MARK_PATH`
// (WorkHomeMark.tsx) by a script that parsed the path, cut the contours at
// y = TRACE_PEN_CUT_Y and measured every piece's arc length; the numbers are
// data, not hand-tuned. MARK_PATH is EIGHT closed subpaths (635.6 user units)
// and the mark renders the top three quarters of the logo (viewBox y -2..36,
// bottom fade), so the two contours are CUT just past the crop's bottom edge
// and every piece below it is dropped.
//
// The pieces are grouped into PENS that run CONCURRENTLY over one clock and
// finish together, so the outline reads as three strokes closing on each
// other at the top rather than one line being traced:
//   • 'left'  — the outer contour's left half, from its bottom-left cut up to
//               the top-centre notch tip;
//   • 'right' — the mirror: the outer contour's right half, up to the same tip;
//   • 'inner' — the inner contour, split at ITS bottom into two halves that
//               rise from both cuts and close at the inner notch tip (one
//               stroke closing on itself, the third meeting at the top);
//   • 'flick' — the lobe pair loops and the four small rings, drawn as brief
//               concurrent flicks over the last three quarters of the clock
//               (the bottom lobes appear as the arcs of them inside the crop).
// Each pen's speed is its own length over the shared duration, so all three
// arrive at the top centre on the same frame. Each piece is its own stroke
// group; see `tracePieceSchedule` in `work-home-mark-motion.ts`. Adjacent
// pieces share exact endpoints (the halves meet at the notch tips), and the
// loops stay closed.
export type WorkHomeMarkTracePen = 'left' | 'right' | 'inner' | 'flick';

export interface WorkHomeMarkTracePiece {
  id: string;
  pen: WorkHomeMarkTracePen;
  d: string;
  /** Arc length in viewBox user units (numerical); sets the pen's speed. */
  length: number;
}

export const TRACE_PEN_PIECES: readonly WorkHomeMarkTracePiece[] = [
  {
    id: 'outer-left',
    pen: 'left',
    d: 'M0.37 37C0.175 36.167 0.054 35.306 0.015 34.426C-0.145 30.841 1.029 27.538 3.085 24.955C3.528 24.399 3.528 23.604 3.085 23.046C1.029 20.465 -0.145 17.16 0.015 13.575C0.341 6.294 6.258 0.374 13.57 0.018C17.15 -0.158 20.461 0.982 23.055 2.995C23.333 3.21 23.668 3.317 24.003 3.317',
    length: 57.2,
  },
  {
    id: 'outer-right',
    pen: 'right',
    d: 'M47.63 37C47.825 36.167 47.946 35.306 47.985 34.426C48.145 30.841 46.97 27.535 44.914 24.955C44.471 24.397 44.471 23.602 44.914 23.046C46.97 20.468 48.145 17.16 47.985 13.575L47.985 13.573C47.663 6.291 41.747 0.373 34.434 0.018C30.854 -0.158 27.544 0.984 24.95 2.995C24.673 3.21 24.338 3.317 24.003 3.317',
    length: 57.2,
  },
  {
    id: 'inner-right',
    pen: 'inner',
    d: 'M44.684 37C44.745 36.807 44.801 36.611 44.852 36.414C46.031 31.8 44.489 27.466 41.487 24.665C41.1 24.305 41.1 23.697 41.487 23.337C44.489 20.536 46.034 16.201 44.852 11.587C43.807 7.507 40.575 4.29 36.476 3.252C31.837 2.077 27.485 3.614 24.671 6.604C24.49 6.797 24.247 6.893 24.003 6.893',
    length: 53.8,
  },
  {
    id: 'inner-left',
    pen: 'inner',
    d: 'M3.322 37C3.261 36.807 3.206 36.611 3.155 36.414C1.973 31.801 3.517 27.468 6.519 24.665C6.906 24.305 6.906 23.697 6.519 23.337C3.517 20.536 1.975 16.201 3.155 11.587C4.202 7.507 7.434 4.292 11.531 3.252C16.169 2.077 20.522 3.612 23.336 6.604C23.517 6.797 23.76 6.893 24.003 6.893',
    length: 53.8,
  },
  {
    id: 'top-pair',
    pen: 'flick',
    d: 'M34.381 7.213C37.258 7.213 40.476 9.808 40.476 13.277C40.475 16.146 39.039 18.649 36.535 20.146C34.148 21.574 31.221 22.459 24.003 22.459C16.785 22.459 13.857 21.574 11.471 20.146C8.967 18.649 7.531 16.143 7.531 13.277C7.531 9.807 10.751 7.213 13.626 7.213C16.08 7.213 18.237 8.449 19.545 10.602C20.574 12.297 21.252 14.18 21.468 18.015C21.513 18.848 22.2 19.503 23.035 19.515C23.346 19.521 23.668 19.52 24.001 19.52C24.334 19.52 24.654 19.519 24.967 19.515C25.802 19.503 26.487 18.848 26.534 18.015C26.75 14.182 27.426 12.299 28.457 10.602C29.763 8.449 31.922 7.213 34.376 7.213L34.381 7.213Z',
    length: 97.83,
  },
  {
    id: 'top-left-ring',
    pen: 'flick',
    d: 'M13.619 10.154C12.203 10.154 10.476 11.595 10.476 13.279C10.476 14.342 10.8 16.324 12.984 17.63L12.984 17.632L12.986 17.632C13.966 18.217 15.161 18.796 17.606 19.156C18.108 19.231 18.55 18.831 18.525 18.327C18.352 15.002 17.818 13.455 17.012 12.124C16.23 10.834 15.035 10.154 13.619 10.154Z',
    length: 27.0,
  },
  {
    id: 'top-right-ring',
    pen: 'flick',
    d: 'M34.376 10.151C32.937 10.151 31.765 10.834 30.983 12.122C30.175 13.453 29.643 15.002 29.47 18.325C29.445 18.827 29.887 19.226 30.389 19.154C32.831 18.796 34.029 18.218 35.009 17.63L35.011 17.627C37.192 16.321 37.52 14.342 37.52 13.277C37.52 11.595 35.815 10.151 34.376 10.151Z',
    length: 27.01,
  },
  {
    id: 'bottom-pair-0',
    pen: 'flick',
    d: 'M19.779 37C20.679 35.398 21.269 33.522 21.469 29.987C21.516 29.154 22.2 28.499 23.035 28.487C23.348 28.483 23.668 28.48 24.001 28.48C24.334 28.48 24.655 28.48 24.968 28.487C25.803 28.499 26.489 29.154 26.534 29.987C26.734 33.524 27.326 35.4 28.224 37',
    length: 21.27,
  },
  {
    id: 'bottom-pair-1',
    pen: 'flick',
    d: 'M8.013 37C7.704 36.305 7.527 35.539 7.527 34.725C7.527 31.856 8.963 29.352 11.467 27.855C13.853 26.427 16.781 25.542 23.999 25.542C31.217 25.542 34.145 26.427 36.531 27.855C39.035 29.352 40.472 31.858 40.472 34.725C40.472 35.539 40.294 36.305 39.986 37',
    length: 47.09,
  },
  {
    id: 'bottom-left-ring',
    pen: 'flick',
    d: 'M11.603 37C10.951 36.423 10.483 35.607 10.483 34.727C10.483 33.661 10.81 31.682 12.991 30.376L12.993 30.376L12.993 30.372C13.973 29.784 15.171 29.206 17.614 28.848C18.116 28.776 18.557 29.174 18.532 29.676C18.359 32.999 17.828 34.549 17.02 35.88C16.748 36.327 16.43 36.702 16.069 37',
    length: 22.13,
  },
  {
    id: 'bottom-right-ring',
    pen: 'flick',
    d: 'M31.949 37C31.585 36.702 31.262 36.326 30.99 35.877C30.184 34.546 29.651 32.997 29.477 29.674C29.453 29.17 29.893 28.772 30.395 28.846C32.841 29.206 34.036 29.785 35.016 30.37L35.019 30.37L35.019 30.372C37.202 31.678 37.526 33.66 37.526 34.723C37.526 35.606 37.051 36.423 36.394 37',
    length: 22.15,
  },
];

// The viewBox row the contours are cut at — just past the crop's bottom edge
// (viewBox '-2 -2 52 38' ends at y = 36), so every cut end sits outside the
// visible box and the pens are born under the fade.
export const TRACE_PEN_CUT_Y = 37;
